import type {
  CyanOperationalSession,
  CyanOperationalSessionService,
} from "./cyanOperationalSessionService";

import {
  detectCyanTakeoverIntent,
  type CyanTakeoverIntent,
  isCyanOwnerActivationConfirmation
} from "./cyanTakeoverIntent";

import type { WorkPackage } from "./agentExecutionContract";

export interface CyanTakeoverChatContext {
  readonly requestedBy: string;

  readonly conversationId?: string;

  readonly projectId?: string;

  readonly repository?: string;

  readonly branch?: string;

  readonly headSha?: string;
}

export interface CyanTakeoverChatResult {
  readonly handled: boolean;

  readonly intent: CyanTakeoverIntent;

  readonly session: CyanOperationalSession | null;

  readonly summary: string;
}

export interface AppendWorkFactory {
  create(
    objective: string,
    session: CyanOperationalSession,
  ): readonly WorkPackage[];
}

/**
 * Conversation-facing orchestration boundary.
 *
 * Cyan interprets and delegates.
 * It does not execute shell/Git/database/infrastructure work here.
 */
export class CyanTakeoverChatController {
  constructor(
    private readonly sessions: CyanOperationalSessionService,

    private readonly appendFactory: AppendWorkFactory,
  ) {}

  async handle(
    message: string,

    currentSession: CyanOperationalSession | null,

    context: CyanTakeoverChatContext,
  ): Promise<CyanTakeoverChatResult> {
    const intent = detectCyanTakeoverIntent(message);

    // CONTEXTUAL_OWNER_CONFIRMATION_HANDOFF
    //
    // Natural confirmation is operational only when this conversation
    // already has a prepared, not-yet-activated operational session.
    //
    // A standalone "yes" with no current session remains normal conversation.
    if (
      currentSession &&
      !currentSession.programId &&
      isCyanOwnerActivationConfirmation(message)
    ) {
      const session = await this.sessions.activate({
        session: currentSession,
        requestedBy: context.requestedBy,
        conversationId: context.conversationId,
        projectId: context.projectId,
        repository: context.repository,
        branch: context.branch,
        headSha: context.headSha,
      });

      return {
        handled: true,
        intent: {
          type: "ACTIVATE",
        },
        session,
        summary: [
          "Mission submitted to the execution system.",
          `Program: ${session.programId ?? "unknown"}.`,
          `State: ${session.view.state}.`,
          "Agents are working in the background.",
          "Cyan remains available for conversation.",
        ].join(" "),
      };
    }

    if (intent.type === "NONE") {
      return {
        handled: false,

        intent,

        session: currentSession,

        summary: "",
      };
    }

    if (intent.type === "PREPARE") {
      const session = await this.sessions.prepare({
        ownerGoal: {
          text: intent.objective,

          projectId: context.projectId ?? null,

          conversationId: context.conversationId ?? null,
        },

        objective: intent.objective,
      });

      return {
        handled: true,

        intent,

        session,

        summary: [
          "Project takeover prepared.",
          `State: ${session.view.state}.`,
          `Checklist items: ${session.view.checklist.length}.`,
          `Phases: ${session.view.phases.length}.`,
          `Owner approvals required: ${session.view.approvalRequired.length}.`,
          `Blocked items: ${session.view.blocked.length}.`,
        ].join(" "),
      };
    }

    if (!currentSession) {
      return {
        handled: true,

        intent,

        session: null,

        summary:
          "No operational takeover session exists yet. Ask Cyan to analyze the project first.",
      };
    }


    if (intent.type === "ACTIVATE") {
      const session = await this.sessions.activate({
        session: currentSession,

        requestedBy: context.requestedBy,

        conversationId: context.conversationId,

        projectId: context.projectId,

        repository: context.repository,

        branch: context.branch,

        headSha: context.headSha,
      });

      return {
        handled: true,

        intent,

        session,

        summary: [
          "Execution program activated.",
          `Program: ${session.programId ?? "unknown"}.`,
          `State: ${session.view.state}.`,
          `Progress: ${session.view.progressPercent}%.`,
        ].join(" "),
      };
    }

    if (intent.type === "REFRESH") {
      const session = await this.sessions.refresh(currentSession);

      return {
        handled: true,

        intent,

        session,

        summary: [
          `Program state: ${session.view.state}.`,
          `Progress: ${session.view.progressPercent}%.`,
          `Queue items: ${session.view.queue.length}.`,
        ].join(" "),
      };
    }

    const additions = this.appendFactory.create(
      intent.objective,
      currentSession,
    );

    if (!additions.length) {
      return {
        handled: true,

        intent,

        session: currentSession,

        summary:
          "The new request requires clarification before it can be added safely to the execution queue.",
      };
    }

    const session = await this.sessions.append(currentSession, additions);

    return {
      handled: true,

      intent,

      session,

      summary: [
        `${additions.length} new work item(s) added to the active program.`,
        `Current progress: ${session.view.progressPercent}%.`,
      ].join(" "),
    };
  }
}
